import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ElementRef,
  HostListener,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { CheckboxTheme } from 'app/services/api/models/checkbox-theme';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'dropdown-checkbox',
  template: `
    <label
      [for]="controlName"
      class="text-sm font-medium text-gray-700 flex items-center gap-1"
    >
      {{ title }}
      <span *ngIf="isRequired() && title !== ''" class="text-red-500">*</span>
      <mat-icon
        *ngIf="tooltipText"
        svgIcon="mat_outline:info"
        [matTooltip]="tooltipText"
        [matTooltipPosition]="tooltipPosition"
        [matTooltipShowDelay]="showDelay"
        [matTooltipHideDelay]="hideDelay"
        class="text-grey-500 cursor-pointer icon-size-5 relative"
      ></mat-icon>
    </label>
    <div class="flex flex-row">
      <button
        class="rounded-md bg-white py-1 px-1.5 w-full"
        [ngClass]="{
          'border-[2px] border-blue-500 text-blue-500': type == 'primary',
          'border-[1.99px] border-gray-300 text-black': type == 'dark',
          'border-[1px] border-gray-300 text-black': type == 'form',
        }"
      >
        <div class="flex flex-row">
          <mat-select
            #select
            [placeholder]="placeholder"
            [panelClass]="{
              'custome-dropdown-panel-nocheck removecheckbox': !multiple,
              'custome-dropdown-panel dropdowncheckbox': multiple,
            }"
            disableOptionCentering
            [formControl]="controlName"
            [multiple]="multiple"
            (selectionChange)="onSeletSubOption($event)"
          >
            <mat-select-trigger
              class="flex flex-row"
              [ngClass]="{
                'text-blue-500 font-bold ': type == 'primary',
                'text-black font-normal': type == 'dark',
              }"
            >
              <div class="mr-1">{{ selectedTitle }}</div>
              <div>
                <span
                  *ngIf="controlName.value?.length > 0 && !showValue"
                  class="border-2 rounded-full px-2 text-white border-blue-600 bg-blue-600"
                >
                  {{ controlName.value?.length || 0 }}
                </span>

                <span
                  *ngIf="controlName.value?.length > 0 && showValue"
                  class="font-normal px-2"
                  [ngClass]="{
                    'border-blue-600 border-2 text-white rounded-full bg-blue-600 ':
                      type == 'primary',
                    'border-none bg-white text-black ': type == 'dark',
                  }"
                >
                  {{ controlName.value }}
                </span>
              </div>
            </mat-select-trigger>
            <ul *ngIf="!disableAllCheckbox">
              <li>
                <mat-checkbox
                  disableRipple="true"
                  [(ngModel)]="allToggle"
                  [ngModelOptions]="{ standalone: true }"
                  [color]="checkboxConfig.color"
                  (change)="allOptions()"
                >
                  All
                </mat-checkbox>
              </li>
            </ul>
            <ul class="w-full" [ngClass]="{ 'w-full': multiple }">
              <li *ngFor="let item of options">
                <mat-option
                  #allSelected
                  [disabled]="setDisabled(item)"
                  [value]="item['id']"
                >
                  <p class="text-blue-600">{{ item['title'] }}</p>
                </mat-option>
              </li>
            </ul>
          </mat-select>
          <mat-icon
            class="text-black block pb-[4px] mr-[-8px] ml-[-12px] icon-size-5"
            [ngClass]="{
              'text-blue-600': type == 'primary',
              'text-black': type == 'dark',
            }"
            (click)="select.open()"
            [svgIcon]="menuSuffixIcon"
          >
          </mat-icon>
        </div>
      </button>
      <button
        *ngIf="hasClear"
        type="button"
        mat-icon-button
        (click)="clearSelection()"
        class="text-red-600 flex items-center ml-1"
      >
        <mat-icon
          class="text-red-600 icon-size-4.5"
          [svgIcon]="'mat_outline:delete'"
        ></mat-icon>
        <span class="hidden sm:inline text-sm">Clear</span>
      </button>
    </div>
    <div
      class="invalid-feedback"
      *ngIf="controlName.invalid && controlName.touched"
    >
      <div
        *ngIf="controlName.errors?.required"
        class="text-sm text-red-500 mt-1 ml-2"
      >
        This field is required.
      </div>
    </div>
  `,
})
export class DropdownCheckboxComponent implements OnInit, OnDestroy {
  @ViewChild('select') select: MatSelect;
  @Input() public controlName: FormControl = new FormControl('');
  @Output() dropdownCheckbox: EventEmitter<any> = new EventEmitter();
  @Input() public title: string = '';
  @Input() public selectedTitle: string = 'Centre';
  @Input() public placeholder: string = '';
  @Input() public startOption: number = null;
  @Input() public endOption: number = null;
  @Input() public disableAllCheckbox: boolean = false;
  @Input() public multiple: boolean = true;
  @Input() public showValue: boolean = false;
  @Input() public type: string = 'primary';
  @Input() public enableMinLimit: boolean = false;
  @Input() public hasClear: boolean = false;
  @Input() tooltipText: string = '';
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 50;
  @Input() hideDelay: number = 50;
  @Input() menuSuffixIcon: string = 'mat_outline:keyboard_arrow_down';
  @Input() isSettingOption: boolean = false;

  protected _onDestroy = new Subject<void>();
  public allToggle = false;

  public checkboxConfig: CheckboxTheme = {
    name: 'Indeterminate',
    completed: false,
    color: 'primary',
  };

  private selectedValues: any;

  private _options: dropdownCheckboxOptions[] = [];
  @Input()
  set options(val: dropdownCheckboxOptions[]) {
    this._options = val || [];
    if (this.isSettingOption) {
      this.ensureValuesExistInOptions();
    }
    this.updateAllToggle();
  }
  get options(): dropdownCheckboxOptions[] {
    return this._options;
  }

  constructor(private eRef: ElementRef) {}

  ngOnInit(): void {
    if (this.startOption && this.endOption) {
      this.setOptions(this.startOption, this.endOption);
    }

    this.selectedValues = this.controlName.value;

    this.controlName.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((values) => {
        this.selectedValues = values;
        if (this.isSettingOption) {
          this.ensureValuesExistInOptions();
        }
        this.updateAllToggle();
      });

    if (this.enableMinLimit) {
      this.controlName.setValue(this.controlName.value);
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  setOptions(start: number, end: number) {
    const array = [];
    for (let i = start; i <= end; i++) {
      array.push({ title: `${i}`, id: `${i}` });
    }
    this.options = array;
  }

  public allOptions() {
    const selectedValues = this.allToggle
      ? this.options.map((item) => item.id)
      : [];

    this.controlName.setValue(selectedValues);

    this.selectedValues = selectedValues;
    this.updateAllToggle();

    this.dropdownCheckbox.emit({
      source: this.select,
      value: selectedValues,
    });
  }
  private ensureValuesExistInOptions(): void {
    const values = this.controlName?.value;

    if (!values || (Array.isArray(values) && values.length === 0)) {
      return;
    }

    const valueArray = Array.isArray(values) ? values : [values];

    valueArray.forEach((val) => {
      const exists = this._options.some((opt) => opt.id === val);

      if (!exists) {
        this._options = [
          ...this._options,
          {
            id: val,
            title: val, // fallback label
          },
        ];
      }
    });
  }
  public onSeletSubOption(value) {
    const selected = this.select.options
      .filter((item: MatOption) => item.selected)
      .map((item: MatOption) => item.value);
    this.selectedValues = selected;
    this.updateAllToggle();
    this.dropdownCheckbox.emit(value);
  }

  private updateAllToggle(): void {
    if (this.multiple && this.options?.length && this.selectedValues?.length) {
      this.allToggle = this.options.length === this.selectedValues.length;
    } else {
      this.allToggle = false;
    }
  }

  public setDisabled(value: any) {
    if (this.enableMinLimit) {
      if (this.selectedValues?.length === 1) {
        return this.selectedValues[0] === value.id;
      }
    }
    return false;
  }

  public clearSelection(): void {
    this.controlName.setValue([]);
    this.allToggle = false;
    this.dropdownCheckbox.emit();
  }

  public isRequired(): boolean {
    return this.controlName.hasValidator(Validators.required);
  }

  // Close dropdown if click is outside
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (this.select && this.select.panelOpen) {
      const clickInside =
        this.eRef.nativeElement.contains(event.target) ||
        this.select.panel?.nativeElement.contains(event.target);
      if (!clickInside) {
        this.select.close();
      }
    }
  }
}

export interface dropdownCheckboxOptions {
  title: string | number;
  id: string | number;
}
